import { Component, ElementRef, EventEmitter, Input, Output, inject } from '@angular/core';
import {
  attributeLabel,
  extractMessageAttributes,
} from '../../../../utils/utils';
import { smsParts } from '../../../../utils/sms';

/**
 * The personalisation fields used in a message body, as neutral chips. They
 * are read from the text itself (`{{attribute}}` placeholders), so they stay
 * in sync however the field got there — picked, typed or pulled in from a
 * template.
 *
 * Fields fill in per contact, so a line under the chips says the final
 * length, and so the part count, is an estimate.
 *
 * Emits `(remove)` with the attribute key when its chip is dismissed.
 */
@Component({
  selector: 'message-attribute-chips',
  standalone: true,
  imports: [],
  template: `
    @if (attributes.length) {
    <div class="mac">
      <ul class="mac__list" aria-label="Personalisation fields in this message">
        @for (attribute of attributes; track attribute; let i = $index) {
        <li class="bk-chip mac__chip">
          <span class="mac__label">{{ labelFor(attribute) }}</span>
          <button
            type="button"
            class="mac__remove"
            [attr.aria-label]="'Remove ' + labelFor(attribute)"
            (click)="onRemove(attribute, i)"
          >
            <span class="bk-sym" aria-hidden="true">close</span>
          </button>
        </li>
        }
      </ul>
      @if (costHint) {
      <p class="mac__hint">{{ hint }}</p>
      }
    </div>
    }
  `,
  styles: `
    :host {
      display: block;
    }

    .mac {
      margin-top: 10px;
      font-family: var(--bk-font);
    }

    .mac__list {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 6px;
      margin: 0;
      padding: 0;
      list-style: none;
    }

    .mac__chip {
      gap: 4px;
      padding: 0 4px 0 10px;
      color: var(--bk-ink);
    }

    .mac__label {
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .mac__remove {
      position: relative;
      display: inline-grid;
      place-items: center;
      flex-shrink: 0;
      width: 18px;
      height: 18px;
      padding: 0;
      border: none;
      border-radius: 999px;
      background: transparent;
      /* ash, not faint: the only cue for a control, so it keeps 3:1 or more. */
      color: var(--bk-ash);
      cursor: pointer;
      transition: background-color 140ms ease, color 140ms ease, transform 140ms var(--bk-ease-out);
    }

    /* 24px touch target around the 18px circle, without making the chip taller. */
    .mac__remove::before {
      content: '';
      position: absolute;
      inset: -3px;
      border-radius: 999px;
    }

    .mac__remove .bk-sym {
      font-size: 14px;
    }

    .mac__remove:active {
      transform: scale(0.9);
    }

    .mac__remove:focus-visible {
      outline: 2px solid var(--bk-ink);
      outline-offset: 1px;
    }

    @media (hover: hover) and (pointer: fine) {
      .mac__remove:hover {
        background: var(--bk-hover);
        color: var(--bk-ink);
      }
    }

    .mac__hint {
      margin: 8px 0 0;
      font-size: 12px;
      line-height: 1.5;
      color: var(--bk-ash);
    }

    @media (prefers-reduced-motion: reduce) {
      .mac__remove:active {
        transform: none;
      }
    }
  `,
})
export class MessageAttributeChipsComponent {
  /** The message body to derive attribute chips from. */
  @Input() message: string | null = '';

  /** Optional friendly-label overrides keyed by attribute key. */
  @Input() labels: Record<string, string> | null = null;

  /** Show the line that says length (and cost) varies by contact. */
  @Input() costHint: boolean = true;

  /** Emits the attribute key whose chip was dismissed. */
  @Output() remove = new EventEmitter<string>();

  private readonly host: ElementRef<HTMLElement> = inject(ElementRef);

  private hintFor = '';
  private hintText = '';

  /**
   * Emit the removal, then keep keyboard focus in place: the chip and its
   * button go away, so focus moves to the next chip's button (or the one
   * before, or the message field once no chips are left).
   */
  onRemove(attribute: string, index: number): void {
    const hadFocus = this.host.nativeElement.contains(document.activeElement);
    this.remove.emit(attribute);
    if (!hadFocus) return;
    setTimeout(() => {
      const host = this.host.nativeElement;
      const buttons = host.querySelectorAll<HTMLElement>('.mac__remove');
      const target = buttons.length
        ? buttons[Math.min(index, buttons.length - 1)]
        : host.closest('input-field, .bk-field')?.querySelector<HTMLElement>('textarea, input');
      target?.focus();
    });
  }

  get attributes(): string[] {
    return extractMessageAttributes(this.message);
  }

  /** "Length varies by contact. About 2 parts if each field fills about 8 characters." */
  get hint(): string {
    const message = this.message ?? '';
    if (message !== this.hintFor) {
      const { parts, encoding } = smsParts(message);
      const count = Math.max(parts, 1);
      const unicode = encoding === 'Unicode' ? ', in Unicode' : '';
      this.hintFor = message;
      this.hintText =
        `Length varies by contact. About ${count} ${count === 1 ? 'part' : 'parts'}${unicode} ` +
        `if each field fills about 8 characters.`;
    }
    return this.hintText;
  }

  labelFor(attribute: string): string {
    return this.labels?.[attribute] ?? attributeLabel(attribute);
  }
}
